import React, { useState, useEffect } from "react"
import { XMarkIcon, Bars3Icon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import { url, filename } from "../url"
import { useFieldEditorController } from "../controllers/field-file-editor-controller"
import { useFieldDragController } from "../controllers/field-drag-controller"
import ImagePreview from "../preview/image-preview"
import MediaLibrary from "aldoo-ra/MediaLibrary"

export default function ImageEditor({
  onChange,
  value,
  field,
  //if true will show label and description
  showInfo = true,
  //if true the field will be read only
  readOnly = false,
  parentObject,
}) {
  //Field editor controller
  const {
    localValue,
    setLocalValue,
    handleChange,
    onRemoveItem,
    errors,
    setErrors,
  } = useFieldEditorController({
    value,
    field,
    onChange,
    category: "images",
  })

  // Drag controller
  const { handleDragStart, handleDragOver, handleDragEnd } =
    useFieldDragController(localValue, setLocalValue)

  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [currentPreviewImage, setCurrentPreviewImage] = useState(null)
  const [currentPreviewFileName, setCurrentPreviewFileName] = useState(null)
  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false)
  const [selectedImageIndex, setSelectedImageIndex] = useState(null) // Track selected image index for replacement

  const isRequired =
    typeof field.required === "function"
      ? field.required(parentObject)
      : field.required

  // Close preview when 'Escape' key is pressed
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isPreviewOpen) {
        closePreview()
      }
    }

    document.addEventListener("keydown", handleEscape)

    return () => {
      document.removeEventListener("keydown", handleEscape)
    }
  }, [isPreviewOpen])

  const openPreview = (image, fileName, index = null) => {
    setCurrentPreviewImage(image)
    setCurrentPreviewFileName(fileName)

    if (index !== null && index !== undefined) {
      setSelectedImageIndex(index)
    }

    setIsPreviewOpen(true)
  }

  const closePreview = () => {
    setIsPreviewOpen(false)
    setCurrentPreviewImage(null)
    setCurrentPreviewFileName(null)
    // Don't reset selectedImageIndex here, as we might need it for replacement
  }

  // Open Media Library - if index is provided, we're replacing that specific image
  const handleMediaLibraryOpen = (index = null) => {
    if (index !== null && index !== undefined) {
      setSelectedImageIndex(index)
    }

    setIsMediaLibraryOpen(true)
  }

  // Handle file selection from Media Library
  // `fileOrFiles` is either a single file object or an array (multi-select)
  const handleFileSelect = (fileOrFiles) => {
    if (!fileOrFiles) return

    // Normalise to array; if field is single-select, take only the first file
    const incoming = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles]
    const files = field.multiple ? incoming : [incoming[0]]

    const toEntry = (f) => {
      const path = f?.path || f?.url || null
      return path ? { url: path } : null
    }

    // Single file field — always replace with the first entry
    if (!field.multiple) {
      const newFile = toEntry(files[0])
      if (!newFile) return
      setLocalValue(newFile)
      onChange(newFile)
      setSelectedImageIndex(null)
      setIsMediaLibraryOpen(false)
      return
    }

    // Multiple files received (bulk add) — append all to existing array
    if (files.length > 1) {
      const newEntries = files.map(toEntry).filter(Boolean)
      let updatedFiles = []
      if (Array.isArray(localValue)) {
        updatedFiles = [...localValue, ...newEntries]
      } else if (localValue?.url) {
        updatedFiles = [localValue, ...newEntries]
      } else {
        updatedFiles = newEntries
      }
      setLocalValue(updatedFiles)
      onChange(updatedFiles)
      setSelectedImageIndex(null)
      setIsMediaLibraryOpen(false)
      return
    }

    // Single file, multiple field — existing replace/add logic
    const newFile = toEntry(files[0])
    if (!newFile) return

    if (
      selectedImageIndex === 0 &&
      !Array.isArray(localValue) &&
      localValue?.url
    ) {
      setLocalValue(newFile)
      onChange(newFile)
    } else if (
      selectedImageIndex !== null &&
      Array.isArray(localValue) &&
      localValue[selectedImageIndex]
    ) {
      const updatedFiles = JSON.parse(JSON.stringify(localValue))
      updatedFiles[selectedImageIndex] = newFile
      setTimeout(() => {
        setLocalValue(updatedFiles)
        onChange(updatedFiles)
      }, 0)
    } else {
      let updatedFiles = []
      if (Array.isArray(localValue)) {
        updatedFiles = [...localValue, newFile]
      } else if (localValue?.url) {
        updatedFiles = [localValue, newFile]
      } else {
        updatedFiles = [newFile]
      }
      setLocalValue(updatedFiles)
      onChange(updatedFiles)
    }

    setSelectedImageIndex(null)
    setIsMediaLibraryOpen(false)
  }

  const onMediaImageSelect = (image) => {
    // Check if we're getting the image with a selectedIndex from ImagePreview
    const indexFromPreview =
      image.selectedIndex !== undefined ? image.selectedIndex : null
    const isFromPreview = image.fromPreview === true

    if (!image || (!image.url && !image.path)) {
      return
    }

    const filePath = image.path
    const newFile = { url: filePath }

    // Use the index from preview if available, otherwise use the local selectedImageIndex
    const indexToUse =
      indexFromPreview !== null ? indexFromPreview : selectedImageIndex

    // Case 1: Multiple field, but localValue is a single object (replace it)
    if (
      indexToUse === 0 &&
      field.multiple &&
      !Array.isArray(localValue) &&
      localValue &&
      localValue.url
    ) {
      // Just replace the single object
      setLocalValue(newFile)
      onChange(newFile)
    }
    // Case 2: We're replacing a specific image in an array
    else if (
      indexToUse !== null &&
      Array.isArray(localValue) &&
      localValue[indexToUse]
    ) {
      // We're replacing an existing image
      const updatedFiles = [...localValue]
      updatedFiles[indexToUse] = newFile

      // Update the state
      setLocalValue(updatedFiles)
      onChange(updatedFiles)
    }
    // Case 3: Single field (always replace)
    else if (!field.multiple) {
      // Single image field
      setLocalValue(newFile)
      onChange(newFile)
    }
    // Case 4: Multiple field, adding new
    else {
      // Multiple image field - adding new
      let updatedFiles = []

      if (Array.isArray(localValue)) {
        updatedFiles = [...localValue, newFile]
      } else if (localValue && localValue.url) {
        updatedFiles = [localValue, newFile]
      } else {
        updatedFiles = [newFile]
      }

      setLocalValue(updatedFiles)
      onChange(updatedFiles)
    }

    // Update the preview with the new image
    if (isFromPreview) {
      // If it's from the preview, update the preview image but don't close it
      setCurrentPreviewImage(url(filePath))
      setCurrentPreviewFileName(filename(filePath))
    } else {
      // Only close if not coming from preview
      closePreview()
    }
  }

  const renderItem = (item, index) => {
    // Determine size classes based on thumbnailSize field property
    // Default to 32x32 if not specified
    const sizeClasses = field.thumbnailSize === "full"
      ? "w-full aspect-square"
      : field.thumbnailSize
      ? `${field.thumbnailSize}`
      : "w-32 h-32"

    return !item || !item.url ? null : (
      <div
        key={index}
        className={`relative flex-none border border-admin_bordered rounded-md overflow-hidden group ${sizeClasses}`}
        draggable={!readOnly && localValue?.length > 1}
        onDragStart={!readOnly && localValue?.length > 1 ? () => handleDragStart(index) : undefined}
        onDragOver={!readOnly && localValue?.length > 1 ? (e) => {
          e.preventDefault()
          handleDragOver(index)
        } : undefined}
        onDragEnd={!readOnly && localValue?.length > 1 ? handleDragEnd : undefined}
      >
        <img
          src={url(item.url)}
          alt="Uploaded"
          className="w-full h-full object-cover rounded-md cursor-pointer"
          onClick={() => openPreview(url(item.url), filename(item.url), index)}
        />
        {/* Upload progress */}
        {item.progress > 0 && item.progress < 100 && (
          <div
            className="absolute top-0 left-0 right-0 bg-admin_primary"
            style={{ width: `${item.progress}%` }}
          >
            {/* show the progress as a number*/}
            <div className="ml-1 w-full text-xs text-white">
              {item.progress}%
            </div>
          </div>
        )}

        {!readOnly && (
          <div className="absolute top-1 left-1 bg-admin_neutral_2 text-admin_text_inv rounded-full p-1 cursor-move opacity-0 group-hover:opacity-100 transition-opacity">
            <Bars3Icon className="w-4 h-4" />
          </div>
        )}

        {!readOnly && (
          <div className="absolute top-1 right-1 flex space-x-1">
            {/* Replace button */}
            <button
              className="text-white bg-admin_primary rounded-full w-6 h-6 flex items-center justify-center hover:bg-admin_primary_dk focus:outline-none opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleMediaLibraryOpen(index)
              }}
              title="Replace image"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                  clipRule="evenodd"
                />
              </svg>
            </button>

            {/* Delete button */}
            <button
              className="text-white bg-admin_error rounded-full w-6 h-6 flex items-center justify-center hover:bg-admin_error/90 focus:outline-none opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => onRemoveItem(index)}
              title="Remove image"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        )}

        <span className="mt-2 text-center text-xs text-admin_text block">
          {filename(item.url)}
        </span>
      </div>
    )
  }

  // Check if we have any images
  const hasImages = Array.isArray(localValue)
    ? localValue.length > 0 && localValue.some(item => item && item.url)
    : localValue && localValue.url

  return (
    <div className="image-editor">
      {/* Show "Add Image" button only if we have images and field allows multiple */}
      {!readOnly && hasImages && field.multiple && (
        <div className="flex flex-row justify-end items-center mb-2">
          <Button
            variant="admin_secondary"
            onClick={() => handleMediaLibraryOpen(null)} // Pass null to indicate adding new
          >
            Add Image
          </Button>
        </div>
      )}

      {!readOnly && field.multiple && localValue?.length > 0 && (
        <div className="text-sm text-admin_text mb-2 flex flex-row items-center">
          <Button
            variant="admin_secondary"
            className="mr-5"
            onClick={() => setLocalValue([])}
          >
            {`${field.multiple ? "Clear All" : "Clear"}`}
          </Button>
          {field.multiple && (
            <div className="text-sm text-admin_text">
              {localValue.length} {localValue.length === 1 ? "image" : "images"}
            </div>
          )}
        </div>
      )}

      {/* Show placeholder if no images, otherwise show images */}
      {!hasImages && !readOnly ? (
        <div
          className={`flex items-center justify-center border-2 border-dashed border-admin_bordered rounded-lg cursor-pointer hover:border-admin_primary hover:bg-admin_canvas/50 transition-colors ${
            field.thumbnailSize === "full" ? "w-full aspect-square" : "w-32 h-32"
          }`}
          onClick={() => handleMediaLibraryOpen(null)}
        >
          <span className="text-admin_text_secondary text-sm">Select image</span>
        </div>
      ) : (
        <div className="flex gap-4 overflow-x-auto py-4">
          {Array.isArray(localValue)
            ? localValue?.map((value, index) => renderItem(value, index))
            : renderItem(localValue, 0)}
        </div>
      )}

      {errors.length > 0 && (
        <div className="text-error text-center w-full mb-4">
          {errors.map((error, index) => (
            <div key={index}>{error}</div>
          ))}
        </div>
      )}

      {/* Image Preview Component */}
      <ImagePreview
        isOpen={isPreviewOpen}
        imageUrl={currentPreviewImage}
        fileName={currentPreviewFileName}
        onClose={closePreview}
        onImageSelect={onMediaImageSelect}
        selectedImageIndex={selectedImageIndex} // Pass the index to ImagePreview
      />

      {/* Media Library Component with defaultFileType set to "image" */}
      <MediaLibrary
        isOpen={isMediaLibraryOpen}
        onClose={() => {
          setIsMediaLibraryOpen(false)
          setSelectedImageIndex(null) // Reset index when closing without selection
        }}
        onFileSelect={handleFileSelect}
        title={
          selectedImageIndex !== null
            ? "Replace Image"
            : field.multiple
            ? "Add Image"
            : "Select Image"
        }
        defaultFileType="image" // Set the default file type to images
      />
    </div>
  )
}
